import React, {useContext, useEffect, useState} from 'react';
import {H3, Text, Code} from '@blueprintjs/core';
import styled from 'styled-components';

import {SchemaForm} from '../../../components/forms';
import {DataPageContext} from '../context';


const Section = styled.div`
    margin-bottom: 2.5em;
    padding-bottom: 2.5em;
    border-bottom: 3px solid rgba(17, 20, 24, 0.15);
`;

const Description = styled(Text)`
    margin-bottom: 1em;
    color: #5c7080;
    line-height: 1.5;
`;

const SideBySide = styled.div`
    display: flex;
    gap: 1em;
    align-items: flex-start;
    & > * { flex: 1; }
`;


// ============================================================================
// 1. Text & Select Fields
// ============================================================================

const textSelectSchema = {
    type: 'object',
    required: ['name', 'email', 'status'],
    properties: {
        id:        {type: 'string', pattern: '^[a-f\\d]{24}$', readOnly: true},
        name:      {type: 'string'},
        email:     {type: 'string', pattern: '^[^@]+@[^@]+\\.[^@]+$'},
        createdAt: {type: 'string', readOnly: true},
        status:    {type: 'string', enum: ['active', 'inactive', 'suspended', 'pending']},
        timezone:  {type: 'string', enum: ['Europe/London', 'America/New_York', 'Asia/Tokyo', 'Australia/Sydney']},
        theme:     {type: ['string', 'null']},
        organisation: {
            oneOf: [
                {const: 'org_001', title: 'Acme Corp'},
                {const: 'org_002', title: 'Globex Industries'},
                {const: 'org_003', title: 'Initech'},
                {const: 'org_004', title: 'Umbrella Corporation'},
            ],
        },
    },
};

const textSelectUischema = {
    type: 'Group',
    label: 'Text & Select Fields',
    elements: [
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/id', options: {disabled: true}},
            {type: 'Control', scope: '#/properties/status', options: {fill: true}},
            {type: 'Control', scope: '#/properties/createdAt', options: {disabled: true}},
        ]},
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/name'},
            {type: 'Control', scope: '#/properties/organisation', options: {fill: true}},
            {type: 'Control', scope: '#/properties/email'},
        ]},
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/theme', label: 'Theme (optional)'},
            {type: 'Control', scope: '#/properties/timezone', options: {fill: true}},
        ]},
    ],
};

const textSelectData = {
    id: '65a1b2c3d4e5f6a7b8c9d0e1',
    name: 'Jane Doe',
    email: 'jane@example.com',
    createdAt: '2025-06-15T10:30:00Z',
    status: 'active',
    organisation: 'org_002',
    timezone: 'Europe/London',
    theme: null,
};


// ============================================================================
// 2. Multi Select
// ============================================================================

const multiSelectSchema = {
    type: 'object',
    properties: {
        tags: {
            type: 'array',
            items: {type: 'string', enum: ['frontend', 'backend', 'devops', 'design', 'mobile', 'security', 'data', 'QA']},
        },
        permissions: {
            type: 'array',
            items: {
                oneOf: [
                    {const: 'read',   title: 'Read Access'},
                    {const: 'write',  title: 'Write Access'},
                    {const: 'delete', title: 'Delete Access'},
                    {const: 'admin',  title: 'Admin Access'},
                ],
            },
        },
        labels: {
            type: 'array',
            items: {type: 'string', examples: ['bug', 'feature', 'enhancement', 'documentation', 'urgent']},
        },
    },
};

const multiSelectUischema = {
    type: 'Group',
    label: 'Multi Select',
    elements: [
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/tags', options: {icon: 'tag', placeholder: 'Filter tags...'}},
            {type: 'Control', scope: '#/properties/permissions', options: {icon: 'lock'}},
            {type: 'Control', scope: '#/properties/labels', options: {icon: 'label', placeholder: 'Search or create...'}},
        ]},
    ],
};

const multiSelectData = {
    tags: ['frontend', 'design'],
    permissions: ['read', 'write'],
    labels: ['bug', 'custom-label'],
};


// ============================================================================
// 3. Text Area
// ============================================================================

const textareaSchema = {
    type: 'object',
    properties: {
        notes: {type: 'string'},
    },
};

const textareaUischema = {
    type: 'Group',
    label: 'Text Area',
    elements: [
        {type: 'Control', scope: '#/properties/notes', label: 'Notes', options: {multi: true, rows: 4}},
    ],
};

const textareaData = {
    notes: 'Some notes about this record.\nMultiple lines are supported.',
};


// ============================================================================
// 4. Date Picker
// ============================================================================

const datePickerSchema = {
    type: 'object',
    properties: {
        goLiveDate: {type: 'string', format: 'date-time'},
        eventStart: {type: 'string', format: 'date-time'},
        preciseTimestamp: {type: 'string', format: 'date-time'},
    },
};

const datePickerUischema = {
    type: 'Group',
    label: 'Date Picker',
    elements: [
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/goLiveDate', label: 'Go Live Date (date only)'},
            {type: 'Control', scope: '#/properties/eventStart', label: 'Event Start (with time)', options: {time: {enabled: true}}},
            {type: 'Control', scope: '#/properties/preciseTimestamp', label: 'Precise (seconds)', options: {time: {enabled: true, precision: 'second'}}},
        ]},
    ],
};

const datePickerData = {
    goLiveDate: '2025-03-15T00:00:00.000Z',
    eventStart: '2025-06-01T09:30:00.000Z',
    preciseTimestamp: null,
};


// ============================================================================
// 5. Nested Objects
// ============================================================================

const nestedSchema = {
    type: 'object',
    properties: {
        options: {
            type: 'object',
            properties: {
                enableNotifications: {type: 'boolean', default: true},
                darkMode:            {type: 'boolean', default: false},
                betaFeatures:        {type: 'boolean', default: false},
            },
        },
        metadata: {
            type: 'object',
            required: ['version', 'author'],
            properties: {
                version:     {type: 'string', pattern: '^\\d+\\.\\d+\\.\\d+$'},
                author:      {type: 'string'},
                description: {type: 'string'},
            },
        },
        config: {
            type: 'object',
            required: ['host'],
            properties: {
                host:    {type: 'string', pattern: '^https?://'},
                port:    {type: 'integer', minimum: 1, maximum: 65535},
                retries: {type: 'integer', minimum: 0, maximum: 10},
            },
        },
    },
};

const nestedAutoUischema = {
    type: 'HorizontalLayout',
    elements: [
        {type: 'Control', scope: '#', label: 'Auto-nested from root'},
    ],
};

const nestedJsonUischema = {
    type: 'VerticalLayout',
    elements: [
        {type: 'Control', scope: '#', label: 'Same schema (nested: false)', options: {nested: false, rows: 10}},
    ],
};

const nestedData = {
    options:  {enableNotifications: true, darkMode: false, betaFeatures: true},
    metadata: {version: '1.2.0', author: 'Jane Doe', description: 'User profile configuration'},
    config:   {host: 'https://api.example.com', port: 8080, retries: 3},
};


// ============================================================================
// 6. Array of Objects
// ============================================================================

const arraySchema = {
    type: 'object',
    properties: {
        contacts: {
            type: 'array',
            items: {
                type: 'object',
                required: ['name', 'email'],
                properties: {
                    name:   {type: 'string'},
                    email:  {type: 'string', pattern: '^[^@]+@[^@]+\\.[^@]+$'},
                    role:   {type: 'string', enum: ['admin', 'editor', 'viewer']},
                    notify: {type: 'boolean', default: false},
                },
            },
        },
    },
};

const arrayUischema = {
    type: 'Group',
    label: 'Array of Objects',
    elements: [
        {type: 'Control', scope: '#/properties/contacts', options: {horizontal: true, itemTitle: 'name'}},
    ],
};

const arrayData = {
    contacts: [
        {name: 'Alice Smith', email: 'alice@example.com', role: 'admin',  notify: true},
        {name: 'Bob Jones',   email: 'bob@example.com',   role: 'viewer', notify: false},
    ],
};


// ============================================================================
// 7. Dynamic Object
// ============================================================================

const dynamicObjectSchema = {
    type: 'object',
    properties: {
        sections: {
            type: 'object',
            additionalProperties: {
                type: 'object',
                required: ['title'],
                properties: {
                    title:       {type: 'string'},
                    description: {type: 'string'},
                    enabled:     {type: 'boolean', default: true},
                    priority:    {type: 'string', enum: ['low', 'medium', 'high']},
                },
            },
        },
    },
};

const dynamicObjectUischema = {
    type: 'Group',
    label: 'Dynamic Object',
    elements: [
        {type: 'Control', scope: '#/properties/sections', options: {
            allowPropertyRenaming: true,
            itemTitle: 'title',
            itemUISchema: {
                type: 'HorizontalLayout',
                options: {wrap: false},
                elements: [
                    {type: 'Control', scope: '#/properties/enabled', options: {toggle: true, maxWidth: '120px'}},
                    {type: 'Control', scope: '#/properties/title'},
                    {type: 'Control', scope: '#/properties/priority', options: {fill: true, maxWidth: '180px'}},
                    {type: 'Control', scope: '#/properties/description'},
                ],
            },
        }},
    ],
};

const dynamicObjectData = {
    sections: {
        homepage: {title: 'Homepage', description: 'Main landing page section', enabled: true, priority: 'high'},
        analytics: {title: 'Analytics', description: 'Usage tracking and metrics', enabled: true, priority: 'medium'},
        experimental: {title: 'Experimental', description: 'Beta features in testing', enabled: false, priority: 'low'},
    },
};


// ============================================================================
// 8. Collapsible Cards
// ============================================================================

const collapsibleSchema = {
    type: 'object',
    properties: {
        endpoints: {
            type: 'array',
            minItems: 1,
            items: {
                type: 'object',
                required: ['url', 'method'],
                properties: {
                    url:    {type: 'string', minLength: 1},
                    method: {type: 'string', enum: ['GET', 'POST', 'PUT', 'DELETE']},
                    active: {type: 'boolean', default: true},
                },
            },
        },
        headers: {
            type: 'object',
            additionalProperties: {
                type: 'object',
                required: ['value'],
                properties: {
                    value:  {type: 'string', minLength: 1},
                    secret: {type: 'boolean', default: false},
                },
            },
        },
    },
};

const collapsibleUischema = {
    type: 'VerticalLayout',
    elements: [
        {type: 'Group', label: 'API Endpoints', elements: [
            {type: 'Control', scope: '#/properties/endpoints', options: {
                editMode: 'collapsible',
                collapsed: true,
                itemTitle: 'url',
                itemUISchema: {
                    type: 'HorizontalLayout',
                    options: {wrap: false},
                    elements: [
                        {type: 'Control', scope: '#/properties/active', options: {toggle: true, maxWidth: '120px'}},
                        {type: 'Control', scope: '#/properties/method', options: {fill: true, maxWidth: '150px'}},
                    ],
                },
            }},
        ]},
        {type: 'Group', label: 'Custom Headers', options: {editMode: 'collapsible', collapsed: true}, elements: [
            {type: 'Control', scope: '#/properties/headers', options: {
                editMode: 'collapsible',
                collapsed: true,
                allowPropertyRenaming: true,
                itemUISchema: {
                    type: 'HorizontalLayout',
                    options: {wrap: false},
                    elements: [
                        {type: 'Control', scope: '#/properties/value'},
                        {type: 'Control', scope: '#/properties/secret', options: {toggle: true, maxWidth: '120px'}},
                    ],
                },
            }},
        ]},
    ],
};

const collapsibleData = {
    endpoints: [
        {url: 'https://api.example.com/users', method: 'GET', active: true},
        {url: '', method: 'POST', active: false},
        {url: 'https://api.example.com/health', method: 'GET', active: true},
    ],
    headers: {
        Authorization: {value: 'Bearer token123', secret: true},
        'Content-Type': {value: 'application/json', secret: false},
        'X-Custom': {value: '', secret: false},
    },
};


// --- Edit Mode comparison schemas ---

const editModeSchema = {
    type: 'object',
    required: ['name'],
    properties: {
        name:   {type: 'string', minLength: 1},
        role:   {type: 'string', enum: ['viewer', 'editor', 'admin']},
        active: {type: 'boolean'},
    },
};

const editModeUischemaDefault = {
    type: 'Group',
    label: 'Default (always expanded)',
    elements: [
        {type: 'Control', scope: '#/properties/name'},
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/role', options: {fill: true}},
            {type: 'Control', scope: '#/properties/active', options: {maxWidth: 'fit-content'}},
        ]},
    ],
};

const editModeUischemaCollapsible = {
    type: 'Group',
    label: 'Collapsible',
    options: {editMode: 'collapsible', collapsed: true},
    elements: [
        {type: 'Control', scope: '#/properties/name'},
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/role', options: {fill: true}},
            {type: 'Control', scope: '#/properties/active', options: {maxWidth: 'fit-content'}},
        ]},
    ],
};

const editModeUischemaPopover = {
    type: 'Group',
    label: 'Popover (hover edit icon)',
    options: {editMode: 'popover'},
    elements: [
        {type: 'Control', scope: '#/properties/name'},
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/role', options: {fill: true}},
            {type: 'Control', scope: '#/properties/active', options: {maxWidth: 'fit-content'}},
        ]},
    ],
};

const editModeUischemaOverlay = {
    type: 'Group',
    label: 'Overlay (click edit icon)',
    options: {editMode: 'overlay'},
    elements: [
        {type: 'Control', scope: '#/properties/name'},
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/role', options: {fill: true}},
            {type: 'Control', scope: '#/properties/active', options: {maxWidth: 'fit-content'}},
        ]},
    ],
};

const editModeUischemaDrawer = {
    type: 'Group',
    label: 'Drawer (click edit icon)',
    options: {editMode: 'drawer'},
    elements: [
        {type: 'Control', scope: '#/properties/name'},
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/role', options: {fill: true}},
            {type: 'Control', scope: '#/properties/active', options: {maxWidth: 'fit-content'}},
        ]},
    ],
};

const editModeData = {
    name: '',
    role: 'editor',
    active: true,
};


// ============================================================================
// 9. Drag-and-Drop Reordering
// ============================================================================

const dragDropSchema = {
    type: 'object',
    properties: {
        metrics: {
            type: 'array',
            maxItems: 4,
            items: {
                type: 'object',
                required: ['name'],
                properties: {
                    name:     {type: 'string'},
                    unit:     {type: 'string', enum: ['count', 'percent', 'seconds', 'meters']},
                    enabled:  {type: 'boolean', default: true},
                    priority: {type: 'string', enum: ['low', 'medium', 'high']},
                },
            },
        },
        settings: {
            type: 'object',
            additionalProperties: {
                type: 'object',
                required: ['label'],
                properties: {
                    label:   {type: 'string'},
                    visible: {type: 'boolean', default: true},
                },
            },
        },
    },
};

const dragDropVerticalUischema = {
    type: 'Group',
    label: 'Array — Vertical (default)',
    elements: [
        {type: 'Control', scope: '#/properties/metrics', options: {
            itemTitle: 'name',
        }},
    ],
};

const dragDropHorizontalUischema = {
    type: 'Group',
    label: 'Array — Horizontal',
    elements: [
        {type: 'Control', scope: '#/properties/metrics', options: {
            itemTitle: 'name',
            editMode: 'collapsible',
            collapsed: true,
            horizontal: true,
        }},
    ],
};

const dragDropDynamicUischema = {
    type: 'Group',
    label: 'Dynamic Object',
    elements: [
        {type: 'Control', scope: '#/properties/settings', options: {
            allowPropertyRenaming: true,
            itemTitle: 'label',
            itemUISchema: {
                type: 'HorizontalLayout',
                options: {wrap: false},
                elements: [
                    {type: 'Control', scope: '#/properties/visible', options: {toggle: true, maxWidth: '120px'}},
                ],
            },
        }},
    ],
};

const dragDropData = {
    metrics: [
        {name: 'Impressions', unit: 'count',   enabled: true,  priority: 'high'},
        {name: 'Dwell Time',  unit: 'seconds', enabled: true,  priority: 'medium'},
        {name: 'Conversion',  unit: 'percent', enabled: false, priority: 'low'},
        {name: 'Distance',    unit: 'meters',  enabled: true,  priority: 'medium'},
    ],
    settings: {
        dashboard:  {label: 'Dashboard',  visible: true},
        analytics:  {label: 'Analytics',  visible: true},
        reports:    {label: 'Reports',    visible: false},
    },
};


// ============================================================================
// 10. Cascading Select
// ============================================================================

const cascadingLevels = [
    {
        key: 'category',
        label: 'Category',
        items: [
            {value: 'store', label: 'Store'},
            {value: 'internal', label: 'Internal'},
            {value: 'attraction', label: 'Attraction'},
        ],
    },
    {
        key: 'path',
        label: 'Path',
        delimiter: ':',
        itemsFor: {
            store: ['entrance', 'exit', 'checkout'],
            internal: ['queue', 'staff:area', 'staff:break-room'],
            attraction: [
                'products',
                'products:mens',
                'products:womens',
                'products:womens:pants',
                'products:womens:dresses',
                'displays',
                'displays:window',
                'displays:endcap',
            ],
        },
    },
    {
        key: 'metric',
        label: 'Metric',
        itemsFor: {
            attraction: ['impressions', 'engagement', 'dwell', 'conversion'],
        },
    },
];

const cascadingSingleSchema = {
    type: 'object',
    properties: {
        lineContext: {
            type: 'object',
            required: ['category', 'path'],
            properties: {
                category: {type: 'string'},
                path:     {type: 'string'},
                metric:   {type: 'string'},
            },
        },
    },
};

const cascadingSingleUischema = {
    type: 'Group',
    label: 'Single Select',
    elements: [
        {type: 'Control', scope: '#/properties/lineContext', label: 'Line Context', options: {levels: cascadingLevels}},
    ],
};

const cascadingSingleData = {
    lineContext: {category: 'attraction', path: 'products:womens:pants', metric: 'dwell'},
};

const cascadingMultiLevels = [
    {
        key: 'category',
        label: 'Category',
        selectable: false,
        items: [
            {value: 'store', label: 'Store'},
            {value: 'internal', label: 'Internal'},
            {value: 'attraction', label: 'Attraction'},
        ],
    },
    {
        key: 'path',
        label: 'Path',
        delimiter: ':',
        itemsFor: {
            store: ['entrance', 'exit', 'checkout'],
            internal: ['queue', 'staff:area', 'staff:break-room'],
            attraction: [
                'products',
                'products:mens',
                'products:womens',
                'products:womens:pants',
                'products:womens:dresses',
                'displays',
                'displays:window',
                'displays:endcap',
            ],
        },
    },
    {
        key: 'metric',
        label: 'Metric',
        dependsOn: 'category',
        itemsFor: {
            attraction: ['impressions', 'engagement', 'dwell', 'conversion'],
        },
    },
];

const cascadingMultiSchema = {
    type: 'object',
    properties: {
        lineContext: {
            type: 'object',
            properties: {
                category: {type: 'string'},
                path:     {type: 'array', items: {type: 'string'}},
                metric:   {type: 'array', items: {type: 'string'}},
            },
        },
    },
};

const cascadingMultiUischema = {
    type: 'Group',
    label: 'Multi Select (selectable: false, dependsOn)',
    elements: [
        {type: 'Control', scope: '#/properties/lineContext', label: 'Line Context', options: {levels: cascadingMultiLevels, multi: true}},
    ],
};

const cascadingMultiData = {
    lineContext: {category: 'attraction', path: ['products:womens:pants', 'products:mens'], metric: ['dwell', 'impressions']},
};


// ============================================================================
// 10. Validation Errors
// ============================================================================

const errorSchema = {
    type: 'object',
    required: ['name', 'email', 'status', 'tags'],
    properties: {
        name:   {type: 'string'},
        email:  {type: 'string', pattern: '^[^@]+@[^@]+\\.[^@]+$'},
        status: {type: 'string', enum: ['active', 'inactive', 'suspended']},
        tags: {
            type: 'array',
            items: {type: 'string', enum: ['frontend', 'backend', 'devops', 'design']},
        },
    },
};

const errorUischema = {
    type: 'Group',
    label: 'Validation Errors',
    elements: [
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/status', options: {maxWidth: 'fit-content'}},
            {type: 'Control', scope: '#/properties/name'},
            {type: 'Control', scope: '#/properties/tags', options: {icon: 'tag', maxWidth: 'fit-content'}},
            {type: 'Control', scope: '#/properties/email'}
        ]}
    ],
};

const errorData = {
    name: '',
    email: 'not-an-email',
    status: undefined,
    tags: [],
};


// ============================================================================
// 11. Read-Only Mode
// ============================================================================

const readonlySchema = {
    type: 'object',
    properties: {
        name:    {type: 'string'},
        status:  {type: 'string', enum: ['active', 'inactive']},
        enabled: {type: 'boolean'},
    },
};

const readonlyUischema = {
    type: 'Group',
    label: 'Read-Only Mode',
    elements: [
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/enabled', options: {maxWidth: 'fit-content'}},
            {type: 'Control', scope: '#/properties/status', options: {maxWidth: 'fit-content'}},
            {type: 'Control', scope: '#/properties/name'},
        ]},
    ],
};

const readonlyData = {
    name: 'Locked Record',
    status: 'active',
    enabled: true,
};


// ============================================================================
// 12. Theming with styled-components
// ============================================================================

const styledSchema = {
    type: 'object',
    required: ['name'],
    properties: {
        name:   {type: 'string'},
        role:   {type: 'string', enum: ['viewer', 'editor', 'admin']},
        active: {type: 'boolean'},
        department: {
            oneOf: [
                {const: 'eng',       title: 'Engineering'},
                {const: 'design',    title: 'Design'},
                {const: 'product',   title: 'Product'},
                {const: 'marketing', title: 'Marketing'},
            ],
        },
        bio: {type: 'string'},
    },
};

const styledUischema = {
    type: 'Group',
    label: 'Styled Form',
    elements: [
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/name', options: {icon: 'person'}},
            {type: 'Control', scope: '#/properties/role', options: {icon: 'shield', fill: true}},
            {type: 'Control', scope: '#/properties/department', options: {fill: true}},
        ]},
        {type: 'HorizontalLayout', elements: [
            {type: 'Control', scope: '#/properties/bio', options: {multi: true, rows: 3}},
            {type: 'Control', scope: '#/properties/active', options: {toggle: true}},
        ]},
    ],
};

const styledData = {
    name: 'demo_user',
    role: 'editor',
    department: 'eng',
    active: true,
    bio: 'Full-stack developer.',
};

const StyledSchemaForm = styled(SchemaForm)`
    --sf-vertical-gap: 0.75em;
    --sf-horizontal-gap: 1.5em;
    --sf-group-padding: 24px;
    --sf-group-margin: 0.5em;
    --sf-border-radius: 8px;
    --sf-label-color: #8a6d3b;
    --sf-label-size: 0.85em;
    --sf-label-weight: 500;
    --sf-group-label-color: #5642a6;
    --sf-group-label-size: 1.1em;
    --sf-group-background: #fafafa;
    --sf-group-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    --sf-max-width: 700px;
    --sf-text-align: left;
`;


// ============================================================================
// Page component
// ============================================================================

const ComponentsPage = () => {
    const {setLoading, setNav} = useContext(DataPageContext);

    const [textSelectForm,  setTextSelectForm]  = useState(textSelectData);
    const [multiSelectForm, setMultiSelectForm] = useState(multiSelectData);
    const [textareaForm,    setTextareaForm]    = useState(textareaData);
    const [datePickerForm,  setDatePickerForm]  = useState(datePickerData);
    const [nestedForm,      setNestedForm]      = useState(nestedData);
    const [arrayForm,       setArrayForm]       = useState(arrayData);
    const [dynamicObjForm,  setDynamicObjForm]  = useState(dynamicObjectData);
    const [collapsibleForm, setCollapsibleForm] = useState(collapsibleData);
    const [editModeForm,    setEditModeForm]    = useState(editModeData);
    const [dragDropForm,    setDragDropForm]    = useState(dragDropData);
    const [cascadingSingle, setCascadingSingle] = useState(cascadingSingleData);
    const [cascadingMulti,  setCascadingMulti]  = useState(cascadingMultiData);
    const [errorForm,       setErrorForm]       = useState(errorData);
    const [styledForm,      setStyledForm]      = useState(styledData);

    useEffect(() => {
        setLoading({status: false, location: 'Components'});
        setNav({right: []});
        return () => setNav({});
    }, []);

    return (
        <div>
            {/* --- 1. Text & Select Fields --- */}

            <Section>
                <H3>Text & Select Fields</H3>
                <Description>
                    Text inputs render from <Code>type: "string"</Code>.
                    Disable with <Code>readOnly: true</Code> in the schema
                    or <Code>rule: &#123;effect: "DISABLE"&#125;</Code> on the control.
                    Use <Code>pattern</Code> for regex validation (email, ID format).
                    Nullable fields (<Code>type: ["string", "null"]</Code>) convert
                    empty strings to <Code>null</Code>.
                    Selects render from <Code>enum</Code> (label = value)
                    or <Code>oneOf: [&#123;const, title&#125;]</Code> (display label differs from stored value).
                    Required selects omit the empty option.
                    Selects default to fit-content width so they pair well alongside text inputs.
                    Set <Code>options: &#123;fill: true&#125;</Code> to make a select expand to full width.
                    Use <Code>options: &#123;minWidth, maxWidth&#125;</Code> to constrain any field's width.
                </Description>
                <SchemaForm
                    schema={textSelectSchema}
                    uischema={textSelectUischema}
                    data={textSelectForm}
                    onChange={({data}) => setTextSelectForm(data)}
                />
            </Section>

            {/* --- 2. Multi Select --- */}

            <Section>
                <H3>Multi Select</H3>
                <Description>
                    Multiple-choice selection for array values.
                    Schema <Code>type: "array"</Code> with <Code>items: &#123;enum&#125;</Code> or <Code>items:
                    &#123;oneOf&#125;</Code> auto-resolves to a multi-select
                    (or force it with <Code>options: &#123;multiselect: true&#125;</Code>).
                    The button text adapts: "No X" when empty, the item label for one selection,
                    "N X" for multiple, "All X" when all selected.
                    Use <Code>options: &#123;icon&#125;</Code> for a left icon
                    and <Code>options: &#123;placeholder&#125;</Code> for filter text.
                    For creatable multi-selects, use <Code>items: &#123;examples&#125;</Code> instead
                    of <Code>enum</Code> — a "Create" option appears when the query doesn't match.
                </Description>
                <SchemaForm
                    schema={multiSelectSchema}
                    uischema={multiSelectUischema}
                    data={multiSelectForm}
                    onChange={({data}) => setMultiSelectForm(data)}
                />
            </Section>

            {/* --- 3. Text Area --- */}

            <Section>
                <H3>Text Area</H3>
                <Description>
                    Multi-line text input for longer content.
                    Set <Code>options: &#123;multi: true&#125;</Code> on the control to render
                    a monospace <Code>TextArea</Code> instead of a single-line input.
                    Optionally set <Code>options: &#123;rows: N&#125;</Code> to control height (default 10).
                </Description>
                <SchemaForm
                    schema={textareaSchema}
                    uischema={textareaUischema}
                    data={textareaForm}
                    onChange={({data}) => setTextareaForm(data)}
                />
            </Section>

            {/* --- 4. Date Picker --- */}

            <Section>
                <H3>Date Picker</H3>
                <Description>
                    Fields with <Code>format: "date-time"</Code> in the schema automatically render as
                    a Blueprint DatePicker in a popover. By default only the date is shown and the time
                    part is zeroed out (<Code>T00:00:00.000Z</Code>).
                    Add <Code>options: &#123;time: &#123;enabled: true&#125;&#125;</Code> to show a time
                    picker (default precision: minute).
                    Set <Code>time: &#123;enabled: true, precision: "second"&#125;</Code> for second-level precision.
                    All values are stored as Zulu timestamps (<Code>YYYY-MM-DDTHH:mm:ss.sssZ</Code>).
                </Description>
                <SchemaForm
                    schema={datePickerSchema}
                    uischema={datePickerUischema}
                    data={datePickerForm}
                    onChange={({data}) => setDatePickerForm(data)}
                />
            </Section>

            {/* --- 5. Nested Objects --- */}

            <Section>
                <H3>Nested Objects</H3>
                <Description>
                    Objects with <Code>type: "object"</Code> and <Code>properties</Code> auto-nest
                    by default — each sub-property renders as its own control inside a Group card.
                    Booleans become switches, strings become text inputs, etc.
                    Use <Code>scope: "#"</Code> to render an entire schema from the root with a single Control.
                    Opt out with <Code>options: &#123;nested: false&#125;</Code> to render as a JSON textarea instead.
                    Both modes validate sub-properties independently and share the same data —
                    edits in either side are reflected in the other.
                </Description>
                <SideBySide>
                    <SchemaForm
                        schema={nestedSchema}
                        uischema={nestedAutoUischema}
                        data={nestedForm}
                        onChange={({data}) => setNestedForm(data)}
                    />
                    <SchemaForm
                        schema={nestedSchema}
                        uischema={nestedJsonUischema}
                        data={nestedForm}
                        onChange={({data}) => setNestedForm(data)}
                    />
                </SideBySide>
            </Section>

            {/* --- 6. Array of Objects --- */}

            <Section>
                <H3>Array of Objects</H3>
                <Description>
                    Arrays where <Code>items</Code> has <Code>type: "object"</Code> with <Code>properties</Code> render
                    as a list of cards — one per item. Each card contains a mini-form with the item's
                    nested fields (text inputs, selects, switches, etc.).
                    Items can be added (with defaults from schema) and removed via buttons.
                    Each item validates independently.
                    Use <Code>options: &#123;itemTitle: "name"&#125;</Code> to show an inner field
                    as the card title (editable inline).
                    The contacts array below has <Code>required: ["name", "email"]</Code> and
                    a pattern on email — try clearing a name or entering an invalid email.
                </Description>
                <SchemaForm
                    schema={arraySchema}
                    uischema={arrayUischema}
                    data={arrayForm}
                    onChange={({data}) => setArrayForm(data)}
                />
            </Section>

            {/* --- 7. Dynamic Object --- */}

            <Section>
                <H3>Dynamic Object</H3>
                <Description>
                    Objects with <Code>additionalProperties</Code> in the schema render as a list of
                    editable cards — one per entry. New entries can be added via the "Add item" button,
                    and each entry has a remove button.
                    Set <Code>options: &#123;allowPropertyRenaming: true&#125;</Code> on the Control
                    to make the object keys editable (click the card title to rename).
                    Use <Code>options: &#123;itemTitle: "title"&#125;</Code> to show an inner field
                    as the card title instead of the key.
                    Use <Code>options: &#123;itemUISchema&#125;</Code> to control the layout of each
                    entry's fields, just like with arrays.
                    Below: an object with dynamic sections — each entry shows the title field
                    as the card header, keys are editable, and entries can be added/removed.
                </Description>
                <SchemaForm
                    schema={dynamicObjectSchema}
                    uischema={dynamicObjectUischema}
                    data={dynamicObjForm}
                    onChange={({data}) => setDynamicObjForm(data)}
                />
            </Section>

            {/* --- 8. Edit Modes --- */}

            <Section>
                <H3>Edit Modes</H3>
                <Description>
                    Cards (Groups, array items, dynamic object entries) support
                    an <Code>editMode</Code> option that controls how content is revealed.
                    The default (no editMode) is always expanded with no toggle.
                    Set <Code>editMode: 'collapsible'</Code> for caret-toggle behavior
                    (use <Code>collapsed: true</Code> to start collapsed).
                    Set <Code>editMode: 'popover'</Code> for a hover popover,
                    <Code>'overlay'</Code> for a centered dialog,
                    or <Code>'drawer'</Code> for a side panel.
                    When content is hidden, cards with validation errors show a red error count tag.
                    The name field below is required but empty — notice the error tag on hidden modes.
                </Description>
                <SideBySide>
                    <SchemaForm
                        schema={editModeSchema}
                        uischema={editModeUischemaDefault}
                        data={editModeForm}
                        onChange={({data}) => setEditModeForm(data)}
                    />
                    <SchemaForm
                        schema={editModeSchema}
                        uischema={editModeUischemaCollapsible}
                        data={editModeForm}
                        onChange={({data}) => setEditModeForm(data)}
                    />
                </SideBySide>
                <SideBySide>
                    <SchemaForm
                        schema={editModeSchema}
                        uischema={editModeUischemaPopover}
                        data={editModeForm}
                        onChange={({data}) => setEditModeForm(data)}
                    />
                    <SchemaForm
                        schema={editModeSchema}
                        uischema={editModeUischemaOverlay}
                        data={editModeForm}
                        onChange={({data}) => setEditModeForm(data)}
                    />
                </SideBySide>
                <SideBySide>
                    <SchemaForm
                        schema={editModeSchema}
                        uischema={editModeUischemaDrawer}
                        data={editModeForm}
                        onChange={({data}) => setEditModeForm(data)}
                    />
                    <div />
                </SideBySide>
                <Description style={{marginTop: '1.5em', marginBottom: 0}}>
                    Below: collapsible mode on array items and groups. "API Endpoints" is expanded
                    but its items start collapsed (the second endpoint has an empty URL — expand
                    it to see the error). "Custom Headers" starts fully collapsed.
                </Description>
                <SchemaForm
                    schema={collapsibleSchema}
                    uischema={collapsibleUischema}
                    data={collapsibleForm}
                    onChange={({data}) => setCollapsibleForm(data)}
                />
            </Section>

            {/* --- 9. Drag-and-Drop Reordering --- */}

            <Section>
                <H3>Drag-and-Drop Reordering</H3>
                <Description>
                    Arrays and dynamic objects support drag-and-drop reordering via drag handles.
                    Grab the handle icon on the left of a card title to reorder items.
                    Reordering is enabled by default — disable
                    with <Code>options: &#123;reorderable: false&#125;</Code>.
                    Drag handles are hidden when the form
                    is <Code>readonly</Code> or the field is <Code>disabled</Code>.
                    Collapse state is preserved through reorder operations.
                    Works with both vertical (default) and horizontal item layouts.
                </Description>
                <SideBySide>
                    <SchemaForm
                        schema={dragDropSchema}
                        uischema={dragDropVerticalUischema}
                        data={dragDropForm}
                        onChange={({data}) => setDragDropForm(data)}
                    />
                    <SchemaForm
                        schema={dragDropSchema}
                        uischema={dragDropHorizontalUischema}
                        data={dragDropForm}
                        onChange={({data}) => setDragDropForm(data)}
                    />
                </SideBySide>
                <div style={{marginTop: '1em'}} />
                <SchemaForm
                    schema={dragDropSchema}
                    uischema={dragDropDynamicUischema}
                    data={dragDropForm}
                    onChange={({data}) => setDragDropForm(data)}
                />
            </Section>

            {/* --- 10. Cascading Select --- */}

            <Section>
                <H3>Cascading Select</H3>
                <Description>
                    Multi-level dependent selector for hierarchical data.
                    Add <Code>options: &#123;levels: [...]&#125;</Code> to a Control
                    whose schema is <Code>type: "object"</Code>.
                    Each level defines a <Code>key</Code> (stored in the value object),
                    a <Code>label</Code> (panel header), and either fixed <Code>items</Code> or
                    dependent <Code>itemsFor</Code> (keyed by ancestor level values).
                    Levels with a <Code>delimiter</Code> auto-group flat strings into an
                    indented tree.
                    Default mode is single-select (values are strings, changing a parent clears children).
                    Add <Code>options: &#123;multi: true&#125;</Code> for multi-select
                    (values are arrays, checkboxes, All/Clear buttons).
                </Description>
                <SideBySide>
                    <SchemaForm
                        schema={cascadingSingleSchema}
                        uischema={cascadingSingleUischema}
                        data={cascadingSingle}
                        onChange={({data}) => setCascadingSingle(data)}
                    />
                    <SchemaForm
                        schema={cascadingMultiSchema}
                        uischema={cascadingMultiUischema}
                        data={cascadingMulti}
                        onChange={({data}) => setCascadingMulti(data)}
                    />
                </SideBySide>
            </Section>

            {/* --- 10. Validation Errors --- */}

            <Section>
                <H3>Validation Errors</H3>
                <Description>
                    Errors are computed on every change and shown inline
                    with <Code>intent="danger"</Code>.
                    Add fields to <Code>required</Code> for non-empty validation
                    and <Code>pattern</Code> for regex checks.
                    Below: required text (empty), pattern mismatch (bad email),
                    required select (no value), and required multi-select (empty array).
                    Consumers receive errors
                    as <Code>[&#123;instancePath, message, keyword&#125;]</Code> in
                    the <Code>onChange</Code> callback.
                </Description>
                <SchemaForm
                    schema={errorSchema}
                    uischema={errorUischema}
                    data={errorForm}
                    onChange={({data}) => setErrorForm(data)}
                />
            </Section>

            {/* --- 11. Read-Only Mode --- */}

            <Section>
                <H3>Read-Only Mode</H3>
                <Description>
                    Pass <Code>readonly=&#123;true&#125;</Code> to disable the entire form.
                    All fields become non-interactive. This is independent of per-field
                    disabling via <Code>readOnly</Code> in the schema
                    or <Code>rule.effect: "DISABLE"</Code> on individual controls.
                </Description>
                <SchemaForm
                    schema={readonlySchema}
                    uischema={readonlyUischema}
                    data={readonlyData}
                    readonly={true}
                    onChange={() => {}}
                />
            </Section>

            {/* --- 12. Theming --- */}

            <Section>
                <H3>Theming with styled-components</H3>
                <Description>
                    Wrap <Code>SchemaForm</Code> with <Code>styled(SchemaForm)</Code> and
                    override CSS custom properties.
                    Available: <Code>--sf-vertical-gap</Code>, <Code>--sf-horizontal-gap</Code> (spacing),{' '}
                    <Code>--sf-group-padding</Code>, <Code>--sf-group-margin</Code>, <Code>--sf-group-background</Code>, <Code>--sf-group-shadow</Code> (group cards),{' '}
                    <Code>--sf-group-label-color</Code>, <Code>--sf-group-label-size</Code> (group headings),{' '}
                    <Code>--sf-label-color</Code>, <Code>--sf-label-size</Code>, <Code>--sf-label-weight</Code> (field labels),{' '}
                    <Code>--sf-border-radius</Code> (inputs + cards),{' '}
                    <Code>--sf-max-width</Code>, <Code>--sf-width</Code> (form sizing),{' '}
                    <Code>--sf-text-align</Code> (input alignment).
                    Per-control sizing is available via <Code>options</Code>:{' '}
                    <Code>fill: true</Code> makes selects expand to full width (default: fit-content),{' '}
                    <Code>minWidth</Code> and <Code>maxWidth</Code> constrain any field's width.
                </Description>
                <StyledSchemaForm
                    schema={styledSchema}
                    uischema={styledUischema}
                    data={styledForm}
                    onChange={({data}) => setStyledForm(data)}
                />
            </Section>
        </div>
    );
};

export {ComponentsPage};
